import React from "react";

import { NormalizedScheduledGame, ScheduledGameStatusReason } from "../types";
import { formatGameDateTimeEt, formatGameLocalTime } from "../../../shared/utils/formatters";

interface ScheduledGameCardProps {
  game: NormalizedScheduledGame;
}

const STATUS_COPY: Record<ScheduledGameStatusReason, string> = {
  upcoming: "This game hasn't started yet. Predictions will be available closer to first pitch.",
  in_progress: "This game is currently in progress.",
  completed: "This game has completed.",
};

const STATUS_LABEL: Record<ScheduledGameStatusReason, string> = {
  upcoming: "Scheduled",
  in_progress: "Live",
  completed: "Final",
};

function getMatchupText(game: NormalizedScheduledGame): string {
  if (game.awayTeam && game.homeTeam) {
    return `${game.awayTeam} @ ${game.homeTeam}`;
  }

  if (game.awayTeam) {
    return `${game.awayTeam} @ Home Team TBD`;
  }

  if (game.homeTeam) {
    return `Away Team TBD @ ${game.homeTeam}`;
  }

  return `Game ${game.gameId}`;
}

export function ScheduledGameCard({ game }: ScheduledGameCardProps): JSX.Element {
  const statusText = STATUS_COPY[game.statusReason] ?? STATUS_COPY.upcoming;
  const statusLabel = STATUS_LABEL[game.statusReason] ?? STATUS_LABEL.upcoming;
  const localGameTime = formatGameLocalTime(game.gameStartTimeUtc, game.venueTimeZone);

  const chipClass =
    game.statusReason === "in_progress"
      ? "scheduled-chip-live"
      : game.statusReason === "completed"
        ? "scheduled-chip-final"
        : "scheduled-chip-upcoming";

  return (
    <article className="withheld-card scheduled-card" aria-label={`${statusLabel}: ${getMatchupText(game)}`}>
      <header className="withheld-card-header">
        <h4 className="withheld-card-title">{getMatchupText(game)}</h4>
        <div className="withheld-card-time-block">
          <time className="withheld-card-time">{formatGameDateTimeEt(game.gameDate, game.gameStartTimeUtc)}</time>
          {localGameTime && <div className="withheld-card-local-time">{localGameTime}</div>}
        </div>
      </header>

      <p className="withheld-card-message">{statusText}</p>

      <div className="withheld-card-chips" aria-label="Game status">
        <span className={`withheld-chip ${chipClass}`}>
          {statusLabel}
        </span>
      </div>
    </article>
  );
}
